import React, { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ToggleButtonGroup from '../../forms/ToggleButtonGroup';
import { TripLegType } from '../../../data/models/reservation.model';
import { ServicePlan } from '../../../data/models/service-plan.model';
import {
  Box,
  Button,
  FormHelperText,
  Typography as MuiTypography,
  Paper,
} from '@mui/material';
import { TripPurpose } from '../../../data/models/trip-purpose.model';
import { TimeZone } from '../../../data/models/time-zone.model';
import { TransportationAttribute } from '../../../data/models/transportation-attribute.model';
import { PassengerAddress } from '../../../data/models/passenger.model';
import TripLegForm from './TripLegForm';
import BookTripSupport from '../../member/reservation/BookTripSupport';
import PassengerBenefits from '../passengers/PassengerBenefits';
import { usePortalName } from '../../../hooks/usePortalName';
import Checkbox from '../../forms/Checkbox';
import StandingOrderForm from './StandingOrderForm';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useFeatureStandingOrder } from '../../../hooks/features/useFeatureStandingOrder';
import { useCareLevelMismatch } from '../../../hooks/useCareLevelMismatch';
import * as accountingSlice from '../../../data/accounting.slice';
import { STANDING_ORDER_NOT_ALLOWED_PORTAL_NAMES } from '../../../data/constants';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import passengerUtils from '../../../services/passengerUtils';
import { useFeatureMemberPreferences } from '../../../hooks/features/useFeatureMemberPreferences';
import { useIsClientPortalAdminUser } from '../../../hooks/useIsClientPortalAdminUser';
import {
  useFeatureAddressValidationClientPortal,
  useFeatureAddressValidationMemberPortal,
  useFeatureAddressValidationF1,
} from '../../../hooks/features/useFeatureAddressValidation';
import * as reservationSlice from '../../../data/reservation.slice';

const PREFIX = 'ReservationTripLegForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  actions: `${PREFIX}-actions`,
  helperBlock: `${PREFIX}-helperBlock`,
  benefits: `${PREFIX}-benefits`,
  formControlXS100MD75: `${PREFIX}-formControlXS100MD75`,
  standingOrder: `${PREFIX}-standingOrder`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },

  [`& .${classes.helperBlock}`]: {
    marginTop: 0,
  },

  [`& .${classes.benefits}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 550,
  },

  [`& .${classes.formControlXS100MD75}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
  [`& .${classes.standingOrder}`]: {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px !important',
  },
}));

const Typography = styled(MuiTypography)(({ theme }) => ({
  [`&.${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    marginBottom: theme.spacing(1),
  },
}));

interface ReservationTripLegFormProps {
  clientBC: string;
  tripType: TripLegType;
  tripPurposes: TripPurpose[];
  timeZones: TimeZone[];
  servicePlans: ServicePlan[];
  transportationAttributes: TransportationAttribute[];
  formHook: UseFormReturn<any, any>;
  formHookFields: UseFieldArrayReturn<any, any, 'id'>;
  isSaving: boolean;
  isValid: boolean;
  canSaveAsDraft: boolean;
  isPortalReservation: boolean;
  allowOverride: boolean;
  override: () => void;
  addLeg: () => void;
  next: () => void;
  saveAsDraft: () => void;
  passengerAddresses?: PassengerAddress[];
}

export default function ReservationTripLegForm({
  clientBC,
  tripType,
  tripPurposes,
  timeZones,
  servicePlans,
  transportationAttributes,
  formHook,
  formHookFields,
  isSaving,
  isValid,
  canSaveAsDraft,
  isPortalReservation,
  allowOverride,
  override,
  addLeg,
  next,
  saveAsDraft,
  passengerAddresses,
}: ReservationTripLegFormProps) {
  const { t } = useTranslation('common');
  const portalName = usePortalName() || '';
  const { control, watch } = formHook;
  const isClientPortalAdminUser = useIsClientPortalAdminUser();

  const enableStandingOrders = useFeatureStandingOrder();
  const addressValidationF1Enabled = useFeatureAddressValidationF1();
  const addressValidationMemberPortalEnabled =
    useFeatureAddressValidationMemberPortal();
  const addressValidationClientPortalEnabled =
    useFeatureAddressValidationClientPortal();
  const reservationConfidence = useAppSelector(
    reservationSlice.selectReservationConfidence,
  );
  const loadedReservationConfidence = useAppSelector(
    reservationSlice.selectLoadedReservationConfidence,
  );
  const loadingReservationConfidence = useAppSelector(
    reservationSlice.selectLoadingReservationConfidence,
  );
  const loadingValidations = useAppSelector(
    reservationSlice.selectLoadingReservationValidations,
  );
  const isStandingOrder = watch('StandingOrder');
  const appointmentTime = watch(`MultiLegTrips[0].AppointmentTime`);
  const isMultiLegTrip = tripType === TripLegType.MultiLeg;

  React.useEffect(() => {
    if (tripType !== TripLegType.MultiLeg) {
      let idx = formHookFields.fields.length;
      while (idx > 1) {
        formHookFields.remove(--idx);
      }
    }
  }, [tripType]);

  const tripTypeButtons = [
    { label: t('member.oneWay'), value: TripLegType.OneWay },
    { label: t('member.roundTrip'), value: TripLegType.RoundTrip },
    { label: t('member.multiTrip'), value: TripLegType.MultiLeg },
  ];

  const deleteLeg = (idx: number) => {
    formHookFields.remove(idx);
  };

  const tripTypeHelperText = () => {
    switch (formHook.getValues().TripType) {
      case TripLegType.OneWay:
        return <FormHelperText>{t('member.oneWayHelperText')}</FormHelperText>;
      case TripLegType.RoundTrip:
        return (
          <FormHelperText>{t('member.roundTripHelperText')}</FormHelperText>
        );
      case TripLegType.MultiLeg:
        return (
          <FormHelperText>{t('member.multiLegHelperText')}</FormHelperText>
        );
      default:
        return (
          <FormHelperText>{t('member.roundTripHelperText')}</FormHelperText>
        );
    }
  };

  const reverseTransportationAttributeMap = useAppSelector(
    lookupSlice.selectReverseTransportationAttributeMap,
  );
  const firstLevelAttribute = watch('MultiLegTrips[0].AttributeLevel1');
  const secondLevelAttribute = watch('MultiLegTrips[0].AttributeLevel2');
  const mobilityAttribute = watch('MultiLegTrips[0].MobilityAttribute');

  const isCareLevelMismatch = useCareLevelMismatch(
    {
      attributeLevel1: firstLevelAttribute,
      attributeLevel2: secondLevelAttribute,
      mobilityAttribute,
      transportationAttributes,
    },
    [firstLevelAttribute, secondLevelAttribute, mobilityAttribute],
  );

  const gurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['GURNEY'],
    [reverseTransportationAttributeMap],
  );
  const bariatricGurneyAttribute = useMemo(
    () => reverseTransportationAttributeMap['BARIATRIC_GURNEY'],
    [reverseTransportationAttributeMap],
  );
  const isGurney = useMemo(() => {
    return secondLevelAttribute === gurneyAttribute;
  }, [secondLevelAttribute, gurneyAttribute]);
  const isBariatricGurney = useMemo(() => {
    return secondLevelAttribute === bariatricGurneyAttribute;
  }, [secondLevelAttribute, bariatricGurneyAttribute]);

  const isOverridden = watch('AllowOverride');
  const memberPreferencesEnabled = useFeatureMemberPreferences();

  const disableNext = useMemo(() => {
    if (
      memberPreferencesEnabled &&
      isCareLevelMismatch &&
      portalName &&
      !isClientPortalAdminUser
    ) {
      return true;
    }
    if ((isBariatricGurney || isGurney) && isOverridden) {
      return false;
    }
    if (isBariatricGurney || isGurney) {
      return !isOverridden;
    }
    if (isOverridden) {
      return !isValid;
    }
    if (loadingValidations) {
      return true;
    }
    if (
      (addressValidationF1Enabled && !portalName) ||
      (addressValidationMemberPortalEnabled &&
        portalName &&
        !isClientPortalAdminUser)
    ) {
      if (
        !loadedReservationConfidence ||
        loadingReservationConfidence ||
        reservationConfidence.some((rc) => {
          const dol = rc.tripLegConfidence.dropOffLocationConfidence;
          return dol == null || parseFloat(dol.confidenceScore) < 1;
        })
      ) {
        return true;
      }
    }
    return !isValid;
  }, [
    isBariatricGurney,
    isGurney,
    isValid,
    isOverridden,
    memberPreferencesEnabled,
    isCareLevelMismatch,
    portalName,
    isClientPortalAdminUser,
    addressValidationF1Enabled,
    loadedReservationConfidence,
    loadingReservationConfidence,
    reservationConfidence,
    loadingValidations,
  ]);

  const disableOverride = useMemo(() => {
    if (!isValid && allowOverride) {
      return false;
    }
    if (!isValid) {
      return true;
    }
    if (isSaving) {
      return true;
    }
    if ((isGurney || isBariatricGurney) && allowOverride) {
      return false;
    }
    if (isGurney || isBariatricGurney) {
      return allowOverride;
    }

    return isCareLevelMismatch ? allowOverride : isValid;
  }, [
    isSaving,
    isGurney,
    isBariatricGurney,
    allowOverride,
    isValid,
    isCareLevelMismatch,
  ]);

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.form}>
        <Typography className={classes.title} variant="h2">
          {t('member.tripDetails')}
        </Typography>
        <Box className={classes.formControlXS100MD75}>
          <ToggleButtonGroup
            buttons={tripTypeButtons}
            name="TripType"
            control={formHook.control}
          />
        </Box>
        <Box className={classes.helperBlock}>{tripTypeHelperText()}</Box>
        {formHook.getValues().MultiLegTrips.map((leg: any, idx: number) => {
          return (
            <TripLegForm
              key={idx}
              clientBC={clientBC}
              servicePlans={servicePlans}
              isReservation={true}
              tripType={tripType}
              tripPurposes={tripPurposes}
              timeZones={timeZones}
              index={idx}
              formHook={formHook}
              canDelete={formHookFields.fields.length > 1}
              isPortalReservation={isPortalReservation}
              deleteLeg={deleteLeg}
              transportationAttributes={transportationAttributes}
              passengerAddresses={passengerAddresses}
            />
          );
        })}
        {enableStandingOrders &&
          !isMultiLegTrip &&
          !STANDING_ORDER_NOT_ALLOWED_PORTAL_NAMES.includes(portalName) && (
            <Checkbox
              name="StandingOrder"
              label={t('reservation.standingOrder')}
              className={classes.standingOrder}
              control={control}
            />
          )}
        {enableStandingOrders && isStandingOrder && !isMultiLegTrip && (
          <StandingOrderForm
            appointmentTime={appointmentTime}
            formHook={formHook}
          />
        )}

        <Box className={classes.actions}>
          {tripType === TripLegType.MultiLeg && (
            <Button color="primary" variant="contained" onClick={addLeg}>
              {t('reservation.addLeg')}
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            disabled={disableNext}
            onClick={next}>
            {t('reservation.next')}
          </Button>
          {!isPortalReservation && (
            <>
              <Button
                color="secondary"
                variant="contained"
                disabled={isSaving || !canSaveAsDraft}
                onClick={saveAsDraft}>
                {t('reservation.saveAsDraft')}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                disabled={disableOverride || isOverridden || !disableNext}
                onClick={override}>
                {t('reservation.requestOverride')}
              </Button>
            </>
          )}
        </Box>
      </Paper>
      {isPortalReservation ? (
        <BookTripSupport />
      ) : (
        <>
          <Box className={classes.benefits}>
            <PassengerBenefits
              appointmentDateTime={formHook.getValues(
                'MultiLegTrips[0].AppointmentTime',
              )}
              servicePlans={servicePlans}
            />
          </Box>
        </>
      )}
    </StyledBox>
  );
}
